import {
  LeadValue,
  TenantCommercial,
} from "@inrange/building-manager-api-client";

// One place for how the lead-value band and the tenant commercial profile are
// rendered, because three surfaces show them and the operator sees two of them
// side by side: the tenant block in LeadEditPanel, the /leads/search results
// table, and the /leads inbox. A second copy of these strings is how those
// surfaces drift (see CLAUDE.md -> "Lead pipeline: surfaces that must agree").

export const LEAD_VALUE_HIGH = "high";
export const LEAD_VALUE_MEDIUM = "medium";
export const LEAD_VALUE_LOW = "low";
export const LEAD_VALUE_UNKNOWN = "unknown";
// Not a band: the lookups are still running. Unknown is a settled verdict, so
// the two must never share a label or the operator cannot tell a result from a
// spinner.
export const LEAD_VALUE_PENDING = "pending";

// Bootstrap badge variants. Unknown is deliberately muted rather than red: it
// means nobody has enriched the tenant yet, not that the lead is bad. The text
// colour travels WITH the background rather than being left to each call site -
// `bg="light"` and `bg="warning"` both default to white text, which renders the
// label invisible (the Unknown pill shipped blank in QA for exactly that).
const BADGE_PROPS_BY_VALUE: Record<string, { bg: string; text?: string }> = {
  [LEAD_VALUE_HIGH]: { bg: "success" },
  [LEAD_VALUE_MEDIUM]: { bg: "warning", text: "dark" },
  [LEAD_VALUE_LOW]: { bg: "secondary" },
  [LEAD_VALUE_UNKNOWN]: { bg: "light", text: "dark" },
  [LEAD_VALUE_PENDING]: { bg: "info", text: "dark" },
};

const UNKNOWN_BADGE_PROPS = BADGE_PROPS_BY_VALUE[LEAD_VALUE_UNKNOWN];

export const leadValueBadgeProps = (
  value: LeadValue | null
): { bg: string; text?: string } =>
  BADGE_PROPS_BY_VALUE[value?.leadValue ?? LEAD_VALUE_UNKNOWN] ??
  UNKNOWN_BADGE_PROPS;

export const leadValueLabel = (value: LeadValue | null): string =>
  value?.leadValueLabel ?? "Unknown";

// Sorting reads the server-computed weight so every surface orders the same
// way and the ordering can be re-cut server-side without touching the FE.
export const leadValueSortWeight = (value: LeadValue | null): number =>
  value?.leadValueSort ?? 0;

// A row is pending when either upstream lookup has not run for its tenant yet.
// Both surfaces use this to decide which rows to hand to the resolve LRO and
// whether to keep polling, so "which rows are still working" has one definition.
export const tenantCommercialPending = (
  tenant: TenantCommercial | null | undefined
): boolean => Boolean(tenant?.sizePending || tenant?.footprintPending);

export const leadValueTooltip = (value: LeadValue | null): string => {
  if (!value) {
    return "Lead value: not assessed yet. Nobody has enriched this row's tenant.";
  }
  const reason = value.leadValueReason ? ` ${value.leadValueReason}` : "";
  return `Lead value: ${value.leadValueLabel}.${reason}`;
};

// What each band actually means, in one plain sentence. The band is the tier of
// accounts the company files, which is a fact about a filing rather than an
// estimate - so each line says which accounts, and what that tier implies.
// Only the employee limits are quoted: they're the memorable axis, they were
// unchanged by the 2024 uprating, and a stale money threshold in a tooltip
// would read as authoritative.
const BAND_MEANING: Record<string, string> = {
  large:
    "They file full accounts, which is the tier for companies too big for the" +
    " medium-sized exemption - over 250 employees, or over the turnover and" +
    " balance-sheet limits.",
  medium:
    "They file medium-sized company accounts: up to 250 employees, and under" +
    " the turnover and balance-sheet limits for that tier.",
  small:
    "They file small company accounts: up to 50 employees, and under the" +
    " turnover and balance-sheet limits for that tier.",
  micro:
    "They file micro-entity accounts, the smallest tier: up to 10 employees.",
  dormant:
    "They file dormant accounts, meaning no significant transactions in the" +
    " period. That usually means the trading company is a different entity.",
};

// A company that is no longer on the register is a FINDING, not a failed lookup,
// and the most decisive one on the row: it drives the lead to Low value via the
// `not_active` risk flag. So it is stated wherever it is true, including on a row
// that still carries a size band - a dissolved company can have filed full
// accounts, and "Large" without this reads as a live occupier.
const closedNote = (tenant: TenantCommercial): string => {
  const status = (tenant.companyStatus ?? "").trim().toLowerCase();
  if (!status || status === "active") {
    return "";
  }
  const label = status.replace(/-/g, " ");
  return ` That company is ${label} at Companies House, so it is no longer trading under that registration - the occupier is likely a successor company.`;
};

// Which company we read, and how we came to it. ONE opener for both the banded
// and the no-band branch, because they used to build it separately and drifted:
// the no-band branch dropped the company name entirely whenever no name search
// had run, leaving "This company's latest accounts don't state a size band" -
// naming neither the query nor the answer, which is unfalsifiable.
//
// No searched name means no name search ran: the number came off this row's own
// Companies House matches. Worth saying, because "we searched X" and "this row
// already pointed at X" are different levels of evidence.
const readOpener = (tenant: TenantCommercial): string => {
  // The number is always present here (callers branch on it first), so it is the
  // fallback when the profile fetch gave us no registered name - an operator can
  // still look it up, which "this company" never allowed.
  const found =
    tenant.sizeCompanyName ?? `company number ${tenant.companyNumber}`;
  return tenant.sizeSearchedAs
    ? `Searched Companies House for "${tenant.sizeSearchedAs}" and read ${found}'s accounts.`
    : `Read ${found}'s accounts at Companies House, matched from this row's own Companies House results.`;
};

// The tooltip leads with what we ASKED Companies House, in every case. It is
// the first thing an operator needs whether the band looks wrong or never
// arrived: the row is routinely named after a building or a brand, so the
// question we asked is not obvious from the row, and a wrong answer and a wrong
// question look identical without it.
export const tenantSizeTooltip = (tenant: TenantCommercial | null): string => {
  if (tenant?.sizePending) {
    return "Looking this company up at Companies House.";
  }
  const searched = tenant?.sizeSearchedAs;
  if (!tenant || tenant.sizeBand === "unknown") {
    if (!tenant?.companyNumber) {
      if (searched) {
        return (
          `Searched Companies House for "${searched}" and found no company` +
          " registered under that name - not a dissolved one either - so we" +
          " can't size this business. If that isn't what the tenant is" +
          " registered as, the AI trace shows how we arrived at the name."
        );
      }
      return (
        "We couldn't tell which registered company this tenant is, so there" +
        " was nothing to look up and we can't size the business. That happens" +
        " when the name on the building doesn't clearly belong to one company" +
        " - guessing would give a confident but wrong size."
      );
    }
    return `${readOpener(tenant)} They don't state a size band.${closedNote(tenant)}`;
  }
  // A private company files up to ~9 months after year end, so a band can
  // describe a period that closed well over a year ago - saying so lets a stale
  // band read as stale rather than wrong.
  const asOf = tenant.accountsPeriodEndOn
    ? ` Their latest accounts cover the period to ${tenant.accountsPeriodEndOn}.`
    : "";
  const meaning = BAND_MEANING[tenant.sizeBand]
    ? ` ${BAND_MEANING[tenant.sizeBand]}`
    : "";
  return `${readOpener(tenant)} Size: ${tenant.sizeLabel}.${closedNote(tenant)}${meaning}${asOf}`;
};

export const tenantFootprintTooltip = (
  tenant: TenantCommercial | null
): string => {
  if (tenant?.footprintPending) {
    return "Counting this company's Google Maps listings.";
  }
  if (!tenant || tenant.listingCount === null) {
    return (
      "We haven't been able to count this company's Google Maps listings." +
      " That happens when we can't tell which company this building's name" +
      " refers to - searching the building's own name would just find this one" +
      " site, which would be worse than saying nothing."
    );
  }
  // Naming the searched company matters most on exactly the rows this count is
  // hardest to read: "44 sites" under "Lidl Avonmouth Regional Distribution
  // Centre" only makes sense once you know we counted Lidl.
  const searchedAs = tenant.footprintSearchedAs
    ? ` Counted for "${tenant.footprintSearchedAs}".`
    : "";
  // Google lists a large occupier's departments separately, so the raw count and
  // the site count are both true and the operator should see which one this is -
  // "7 listings" for five buildings is what made a count look invented.
  const raw = tenant.rawListingCount;
  const collapsed =
    raw !== null && tenant.listingCount !== null && raw > tenant.listingCount
      ? ` Google returned ${raw} listings; those sharing a street address are` +
        ` counted once, because a large occupier's departments are listed` +
        ` separately.`
      : "";
  return (
    `${tenant.footprintLabel} found on Google Maps.${searchedAs}${collapsed}` +
    ` This counts places Google knows about, not sites we have verified: Google` +
    ` covers consumer-facing businesses well and industrial or warehousing` +
    ` occupiers poorly, so treat a low count as weak evidence rather than proof` +
    ` of a single location.`
  );
};
